import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import useTranslations from 'src/hooks/useTranslations'
import { faShieldCheck } from '@fortawesome/pro-regular-svg-icons/faShieldCheck'
import { faShieldCheck as faSolidShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck'
import MainMenuItem from 'src/components/Menu/MainMenuItem'
import { isRouteAllowed, reverse } from 'src/routes/config'
import useUserData from 'src/hooks/useUserData'
import {
    CSOD_ADMIN_PERMISSION,
    CSOD_REGISTER_PERMISSION,
    CSOD_VIEW_PERMISSION,
    isAllowed,
    isAnyAllowed,
    SECURITY_ACTION_EXCEPTION_USE,
    SECURITY_ADMIN_VM_RECLASSIFICATION,
    SECURITY_COMPLIANCE_USE,
    SECURITY_DETECT_RESPONSE_USE,
    SECURITY_VULNERABILITY_USE,
    SECURITY_COVERAGE_SCOPE_USE,
    SECURITY_COVERAGE_THREAT_USE,
    SECURITY_COVERAGE_USECASE_USE,
} from 'src/utils/permissions'

/**
 * Renders the SecurityMenu component, which displays the main security navigation menu
 * with nested submenus for Overview, Assess, Admin, and Detect & Response sections.
 * Menu items are conditionally rendered based on user permissions and route availability.
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} props.isOpen - Indicates if the SecurityMenu is open.
 * @param {boolean} props.parentIsCollapsed - Indicates if the parent menu is collapsed.
 * @param {function} props.onClick - Callback function to handle menu click events.
 * @returns {JSX.Element} The rendered SecurityMenu component.
 */
export default function SecurityMenu({ isOpen, parentIsCollapsed, onClick }) {
    const [securityAssessIsOpen, setSecurityAssessIsOpen] = useState(false)
    const [
        securityDetectAndResponseIsOpen,
        setSecurityDetectAndResponseIsOpen,
    ] = useState(false)
    const { t } = useTranslations('MainMenu', 'MainMenu')
    const userData = useUserData()

    const coverageRoute = useMemo(() => {
        if (isAllowed(userData, SECURITY_COVERAGE_SCOPE_USE)) {
            return reverse('coverage-scope')
        }
        if (isAllowed(userData, SECURITY_COVERAGE_THREAT_USE)) {
            return reverse('coverage-detection')
        }
        if (isAllowed(userData, SECURITY_COVERAGE_USECASE_USE)) {
            return reverse('use-case')
        }
        return null
    }, [userData])

    const hasCoveragePermission = useMemo(
        () => !!coverageRoute,
        [coverageRoute]
    )
    const OVERVIEW_ROUTES = Object.freeze(
        [
            'overview',
            'overview-mdr',
            'overview-vulnerability',
            'overview-vulnerability-live-agents',
            'overview-vulnerability-patching-information',
            'overview-vulnerability-urgent-vulnerability',
            'overview-compliance',
            'overview-mpam',
        ].map(route => reverse(route))
    )

    return (
        <MainMenuItem
            icon={faShieldCheck}
            iconActive={faSolidShieldCheck}
            title={t('Security observability')}
            isOpen={isOpen}
            onClick={onClick}
            parentIsCollapsed={parentIsCollapsed}
        >
            {isRouteAllowed(userData, 'overview') && (
                <MainMenuItem
                    title={t('Overview')}
                    to={reverse('overview')}
                    isActive={(_, location) =>
                        OVERVIEW_ROUTES.includes(location?.pathname ?? '')
                    }
                />
            )}
            {isAnyAllowed(userData, [
                SECURITY_VULNERABILITY_USE,
                SECURITY_COMPLIANCE_USE,
                CSOD_VIEW_PERMISSION,
            ]) && (
                <MainMenuItem
                    title={t('Assess')}
                    isOpen={securityAssessIsOpen}
                    onClick={() =>
                        setSecurityAssessIsOpen(!securityAssessIsOpen)
                    }
                    parentIsCollapsed={parentIsCollapsed}
                    subNavigation
                >
                    {isRouteAllowed(userData, 'vulnerability') && (
                        <MainMenuItem
                            title={t('Vulnerability')}
                            to={reverse(
                                window.location.pathname.endsWith('live-agents')
                                    ? 'vulnerability-live-agents'
                                    : 'vulnerability'
                            )}
                        />
                    )}
                    {isRouteAllowed(userData, 'compliance') && (
                        <MainMenuItem
                            title={t('Compliance')}
                            to={reverse('compliance')}
                        />
                    )}
                    {isRouteAllowed(userData, 'csod') && (
                        <MainMenuItem
                            title={t('CSAF')}
                            to={reverse('csod', {
                                subPath: 'csaf',
                            })}
                        />
                    )}
                </MainMenuItem>
            )}
            {isAnyAllowed(userData, [
                CSOD_ADMIN_PERMISSION,
                CSOD_REGISTER_PERMISSION,
                SECURITY_ACTION_EXCEPTION_USE,
                SECURITY_ADMIN_VM_RECLASSIFICATION,
            ]) && (
                <MainMenuItem
                    title={t('Admin')}
                    to={reverse('security-admin')}
                />
            )}
            {isAnyAllowed(userData, [
                SECURITY_DETECT_RESPONSE_USE,
                SECURITY_COVERAGE_SCOPE_USE,
                SECURITY_COVERAGE_THREAT_USE,
                SECURITY_COVERAGE_USECASE_USE,
            ]) && (
                <MainMenuItem
                    title={t('Detect & Response')}
                    isOpen={securityDetectAndResponseIsOpen}
                    onClick={() =>
                        setSecurityDetectAndResponseIsOpen(
                            !securityDetectAndResponseIsOpen
                        )
                    }
                    parentIsCollapsed={parentIsCollapsed}
                    subNavigation={true}
                >
                    {isRouteAllowed(userData, 'mdr-dashboard') && (
                        <MainMenuItem
                            title={t('Dashboard')}
                            to={reverse('mdr-dashboard')}
                        />
                    )}
                    {isRouteAllowed(userData, 'alertsIncidents') && (
                        <MainMenuItem
                            title={t('Alerts & Incidents')}
                            to={reverse(
                                'alertsIncidents',
                                {},
                                {
                                    period: 'days_30',
                                },
                                '^mkdc_scope-details-table_params:(mdrItem:(MDR3:1,MDR4:1))_mkdc$'
                            )}
                        />
                    )}
                    {hasCoveragePermission && (
                        <MainMenuItem
                            title={t('Coverage')}
                            to={coverageRoute}
                        />
                    )}
                    {isRouteAllowed(userData, 'configuration') && (
                        <MainMenuItem
                            title={t('Configuration')}
                            to={reverse('configuration')}
                        />
                    )}
                </MainMenuItem>
            )}
        </MainMenuItem>
    )
}

SecurityMenu.propTypes = {
    isOpen: PropTypes.bool,
    parentIsCollapsed: PropTypes.bool,
    onClick: PropTypes.func,
}
